import { useEffect, useState } from "react";
import type { CompletedItemsPage, Item } from "../api/types";
import { translate } from "../i18n";
import { dayHeader, type Lang } from "../lib/dates";
import { dueDay, sortTasks } from "../lib/tasks";
import { useApp, useSyncState } from "../store";
import type { SyncState } from "../sync/state";
import type { EffectiveViewOptions } from "./viewOptions";

export interface TaskGroup {
  key: string;
  title: string;
  tasks: Item[];
  tone?: string;
}

/** Group tasks for a view's "Grouping" option (sections replace this in project views). */
export function groupTasks(
  list: Item[],
  by: string | null,
  ctx: { state: SyncState; tz: string; today: string; lang: Lang },
): TaskGroup[] | null {
  if (!by) return null;
  const groups = new Map<string, TaskGroup>();
  const add = (key: string, title: string, item: Item, order = key, tone?: string) => {
    let g = groups.get(order);
    if (!g) {
      g = { key, title, tasks: [], tone };
      groups.set(order, g);
    }
    g.tasks.push(item);
  };
  for (const it of list) {
    switch (by) {
      case "due_date": {
        const d = dueDay(it, ctx.tz);
        if (!d) add("none", translate(ctx.lang, "noDate"), it, "~");
        else if (d < ctx.today) add("overdue", translate(ctx.lang, "overdue"), it, "0", "overdue");
        else add(d, dayHeader(d, ctx.today, ctx.lang), it, d);
        break;
      }
      case "added_date": {
        const d = it.added_at.slice(0, 10);
        add(d, dayHeader(d, ctx.today, ctx.lang), it, d);
        break;
      }
      case "priority":
        add(String(it.priority), translate(ctx.lang, "priorityN", { n: 5 - it.priority }), it, String(5 - it.priority));
        break;
      case "label":
        if (!it.labels.length) add("none", translate(ctx.lang, "none"), it, "~");
        for (const l of it.labels) add(l, l, it, `a${l.toLowerCase()}`);
        break;
      case "project": {
        const p = ctx.state.projects[it.project_id];
        add(
          it.project_id,
          p?.inbox_project ? translate(ctx.lang, "inbox") : (p?.name ?? "?"),
          it,
          p?.inbox_project ? "" : (p?.name ?? "~"),
        );
        break;
      }
      default:
        add("all", "", it, "all");
    }
  }
  return [...groups.entries()].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)).map((e) => e[1]);
}

export function applySort(list: Item[], o: EffectiveViewOptions, tz: string): Item[] {
  return o.sortedBy && o.sortedBy !== "manual" ? sortTasks(list, o.sortedBy, o.sortOrder, tz) : list;
}

/** Completed tasks from the REST archive (not part of Sync), refreshed when the store changes. */
export function useCompletedTasks(params: Record<string, string> | null, days = 90): Item[] {
  const client = useApp((s) => s.client);
  const state = useSyncState();
  const [items, setItems] = useState<Item[]>([]);
  const key = params ? JSON.stringify(params) : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch when sync token moves
  useEffect(() => {
    if (!key) {
      setItems([]);
      return;
    }
    let alive = true;
    const until = new Date(Date.now() + 86_400_000).toISOString().replace(/\.\d+Z$/, "Z");
    const since = new Date(Date.now() - days * 86_400_000).toISOString().replace(/\.\d+Z$/, "Z");
    client
      .get<CompletedItemsPage>("/tasks/completed/by_completion_date", {
        since,
        until,
        limit: "200",
        ...JSON.parse(key),
      })
      .then((r) => {
        if (alive) setItems(r.items);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [key, client, state.syncToken, days]);
  // Local view wins (a task uncompleted locally disappears right away).
  return items.filter((i) => {
    const local = state.items[i.id];
    return !local || local.checked;
  });
}
